import {
  boundToOneShop,
  formuleAsks,
  guestChoosesShop,
  productTypeSections,
  textIn,
} from '@croissants/contracts/projections';
import { BackChevron, Button, Card, usePreloadWhenIdle } from '@croissants/ui';
import { type JSX, type ReactNode, useEffect, useState } from 'react';
import { Link, Redirect, useLocation, useSearch } from 'wouter';
import { ChosenMorning } from '../components/ChosenMorning';
import { DeliveryChoice } from '../components/DeliveryChoice';
import { AsyncPanel, ErrorNotice } from '../components/feedback';
import { ItemCard } from '../components/ItemCard';
import { ItemDetail } from '../components/ItemDetail';
import { CartIcon } from '../components/icons';
import { SHOP_CHANGED_PARAM, ShopChangedRedirect } from '../components/ShopChangedRedirect';
import { VoidShopRedirect } from '../components/VoidShopRedirect';
import { useShopFacts, useStorefront, useWindows } from '../hooks/queries';
import { isShopChanged, isVoidShopChoice } from '../lib/api';
import { money } from '../lib/format';
import {
  CHOOSE_PARAM,
  checkoutStep,
  deliveryChoiceAsked,
  deliveryChoiceOffered,
  vitrineBlockedBy,
} from '../lib/journey';
import { heldItemIdsOf, lastLineOf, subtotalMinorUnits } from '../state/basket';
import { useBasket, useRememberItemNames } from '../state/basket-provider';
import { useCheckoutDraft } from '../state/checkout';
import { useLocale } from '../state/locale';
import { useSession } from '../state/session';
import { AFTER_THE_SHELF } from './lazy';

/**
 * How many tiles are fetched eagerly. The grid is two tiles wide on a phone
 * (`minmax(150px, 1fr)`), and a phone shows about three rows under the header,
 * so six covers the first screen and the row a thumb reaches first.
 */
const EAGER_TILES = 6;

/**
 * Mode B2: the guest closed the shut-bakery popup on this visit.
 *
 * Held for the page's life rather than in the component, so coming back to the
 * shelf from the basket does not raise it again, and deliberately NOT stored:
 * the rule is "when the guest scans", and a scan is a fresh page.
 */
let closedNoticeDismissed = false;

/** For the tests, which share one module between cases. */
export function resetClosedNotice(): void {
  closedNoticeDismissed = false;
}

/**
 * The vitrine (PRD GST-003, GST-029, GST-030).
 *
 * Mode A shows products immediately and unbranded: the fulfilling bakery is
 * chosen by the platform and its identity is surfaced nowhere - not here, not on
 * the order, not on the receipt (CAT-009). Mode B shows one named shop's counter,
 * and only after the guest has chosen it, so this screen sends a mode B guest with
 * no shop back to the chooser rather than showing them an empty page.
 */
export function Menu(): JSX.Element {
  usePreloadWhenIdle(AFTER_THE_SHELF);
  const { locale, t, pt } = useLocale();
  const { session } = useSession();
  const { basket, dispatch, count } = useBasket();
  const { draft } = useCheckoutDraft();
  const [, navigate] = useLocation();
  /**
   * The item whose detail sheet is open. Held by id rather than by value, so a
   * refetch that renames or reprices an item is reflected in the open sheet
   * instead of freezing the copy the guest tapped.
   */
  const [openItemId, setOpenItemId] = useState<string | null>(null);

  /* The chooser's mode, and only it: mode B2 shows no house to change. */
  const modeB =
    session !== undefined && session !== null && guestChoosesShop(session.storefrontMode);
  /**
   * Where the arrow beside the title leads, or null when there is nothing
   * behind this screen.
   *
   * Mode B goes back to the chooser - the house is the guest's own choice and
   * they may change it. Mode A and mode B2 have no step before the shelf and
   * draw no arrow: B2's delivery time is changed from the line under the
   * address (GST-045).
   */
  const back: { readonly href: string; readonly label: string } | null = modeB
    ? { href: '/shops', label: t('shops.change') }
    : null;
  const bound = session !== undefined && session !== null && boundToOneShop(session.storefrontMode);
  /**
   * What still has to be settled before there is a catalogue to ask for.
   *
   * `vitrineBlockedBy` is the SAME step order `App.tsx` redirects on
   * (lib/journey.ts), asked again here because this screen is reachable
   * directly - a bookmark, a refresh, a deep link - and must not trust that the
   * redirect ran. Two hand-written copies of the rule is how the two came to
   * disagree; there is one now.
   *
   * Mode A needs the morning: the bakery behind this vitrine is allocated from
   * the chosen window, so without one there is no counter to show (GST-029).
   * Mode B needs only the house - the storefront endpoint answers a `shopId`
   * alone - and the morning is set from the basket bar afterwards (GST-030).
   */
  const blockedBy = vitrineBlockedBy({
    storefrontMode: session?.storefrontMode ?? null,
    shopId: basket.shopId,
    serviceDay: draft.serviceDay,
    windowStart: draft.windowStart,
    orderingModes: session?.orderingModes ?? null,
  });
  /**
   * Where the basket bar goes, and therefore what it says (`checkoutStep`).
   * The morning is asked on the way when none is set - the same rule the basket
   * and the review step apply, stated once so a bar that says "View basket"
   * cannot open a picker.
   */
  const journey = {
    storefrontMode: session?.storefrontMode ?? null,
    shopId: basket.shopId,
    serviceDay: draft.serviceDay,
    windowStart: draft.windowStart,
    orderingModes: session?.orderingModes ?? null,
  };
  const basketStep = checkoutStep(journey, '/basket');

  /**
   * MODE B2's DELIVERY POPUP (founder decision, 2026-09-21; GST-045).
   *
   * While no delivery time is settled the basket bar opens it instead of a
   * screen, and a scan of a SHUT bakery raises it at once, over the shelf:
   * "Your baker is currently closed and opens at ...". `choiceNext` is where the
   * guest lands once a time is settled - the basket when they asked for it, the
   * shelf when the scan did.
   */
  const choiceAsked = deliveryChoiceAsked(journey);
  const facts = useShopFacts(choiceAsked);
  /*
   * The popup's own windows read, asked NOW - beside the facts and the shelf -
   * under the exact key the popup asks under (`useWindows(basket, null)`), so
   * when it opens it reads a cache. Left to the popup, the read went out only
   * once the facts had answered that the shop was shut: a third request in a
   * row on a night scan, after the scan and the shelf (docs/portal-latency-
   * audit.html, eighth pass). Asked only while the popup can be raised, which
   * is also every B2 guest who has not yet chosen a time and will need it.
   */
  useWindows(basket, null, choiceAsked);
  const shutNow =
    facts.data !== undefined &&
    !(facts.data.facts.orderableNow && session?.orderingModes.immediate === true);
  const [choiceNext, setChoiceNext] = useState<string | null>(null);
  const [noticeClosed, setNoticeClosed] = useState(closedNoticeDismissed);
  const raisedByScan = choiceAsked && shutNow && !noticeClosed;
  /* Asked for by the guest it opens whether or not a time is settled: "Change"
     under the address raises the same popup again (founder, 2026-09-21). */
  const choiceOpen = (deliveryChoiceOffered(journey) && choiceNext !== null) || raisedByScan;

  /**
   * "The bakery serving you has changed", shown ONCE (`ShopChangedRedirect`).
   * Read off the URL on the first render and kept in state, then the parameter
   * is stripped - so the sentence survives the strip and not a reload.
   */
  const search = useSearch();
  /* "Change" arrives as `?choose=1` on this screen (`deliveryChoiceHref`):
     open the popup, coming back here, and strip the one-shot parameter. */
  const wantsChoice = new URLSearchParams(search).get(CHOOSE_PARAM) === '1';
  useEffect(() => {
    if (!wantsChoice) return;
    setChoiceNext('/menu');
    navigate('/menu', { replace: true });
  }, [wantsChoice, navigate]);
  const [shopChanged] = useState(() => new URLSearchParams(search).get(SHOP_CHANGED_PARAM) === '1');
  useEffect(() => {
    if (shopChanged) navigate('/menu', { replace: true });
  }, [shopChanged, navigate]);

  const query = useStorefront(
    draft.serviceDay,
    basket.shopId,
    draft.windowStart,
    blockedBy === null,
    heldItemIdsOf(basket),
  );
  // A basket restored from storage, or rebuilt from a past order, learns its
  // labels here rather than carrying ids to the review screen.
  useRememberItemNames(query.data?.items);

  if (blockedBy !== null) return <Redirect to={blockedBy} replace />;

  /*
   * The failure arm separates the server refusing the CHOSEN SHOP
   * (`error.choose_shop`) from every other refusal: the persisted mode B choice
   * names a house this world no longer offers, and the recovery is the chooser
   * rather than a retry that could only repeat it. Windows.tsx meets the same
   * refusal on the picker and answers it with the same component.
   */
  return (
    <AsyncPanel
      query={query}
      error={(failure, retry) =>
        modeB && isVoidShopChoice(failure) ? (
          <VoidShopRedirect />
        ) : /* Mode B2's own refusal: the bound shop has stopped trading, so the
              session is re-resolved onto the next closest one and the basket -
              filled from a shelf that is gone - goes with it. */
        isShopChanged(failure) ? (
          <ShopChangedRedirect />
        ) : (
          <ErrorNotice error={failure} onRetry={retry} />
        )
      }
    >
      {(storefront) => {
        /*
         * One section per TYPE, titled by the words authored for it (CAT-016), in the
         * order the platform sent the items in - which is the running order the Kinds
         * tab sets, applied before the shelf left the server, so this screen and the
         * tab that authored it cannot disagree about either.
         */
        const sections = productTypeSections(storefront.items);
        /* The shelf's running order, so the first tiles a guest sees are fetched
     eagerly and the rest lazily - see `ProductPhotoProps.loading`. */
        const positionOf = (itemId: string): number =>
          storefront.items.findIndex((item) => item.id === itemId);
        /* The words over each section, in THIS reader's language - see
     `sectionTitle`. Every shipped language travels with the heading, so the
     switch moves it in the same render as the tiles under it (NFR-006); the
     served label is the session's language and the fallback. Absent for a kind
     nobody has named on the Kinds tab. */
        const headings: Readonly<Record<string, string>> = Object.fromEntries(
          storefront.sections.map((section) => [
            section.kind,
            textIn(locale, section.labelByLocale, section.label),
          ]),
        );
        /* A product's name for this reader, off the map the item carries: the tile,
     the sheet and the name the basket remembers all read it the same way. */
        const nameOf = (item: {
          name: string;
          nameByLocale?: Readonly<Record<string, string>>;
        }): string => textIn(locale, item.nameByLocale, item.name);
        const openItem = storefront.items.find((item) => item.id === openItemId);
        /* The ITEM, not its price: a set's line costs the card plus the supplements
     of what the guest picked, and `subtotalMinorUnits` is where that is read. */
        const itemOf = (id: string) => storefront.items.find((item) => item.id === id);
        const subtotal = subtotalMinorUnits(basket, itemOf);
        /*
         * DOES THE PLUS OPEN THE SHEET? Only when the card has a question on it
         * (CAT-018). A set of `INCLUDED` sections only asks the guest nothing, so it
         * steps up and down on its tile like any other product - it was opening a
         * sheet whose only control was the Add button, three taps for one croissant
         * box. Read off the card through `formuleAsks`, the same question the sheet
         * and the server ask, never off `formule === undefined`.
         */
        const asksSomething = (item: { formule?: readonly { kind: string }[] }): boolean =>
          formuleAsks(item.formule);

        return (
          <div className="gw-screen">
            {/*
        The house's name and the way back to the chooser - and no blurb. The
        shop's `shortDescription` used to sit here as a paragraph under the
        title: it is the copy that SELLS a house, which is the chooser's job
        (Shops.tsx, where it is still shown on every tile), and by this screen
        the guest has already chosen. Here it only pushed the shelf down.

        The way back is the arrow at the LEFT of the name (founder, 2026-09-18),
        the same mark every portal's heading draws - it was a worded "Change
        bakery" link under the title, which is the shape `PageHeading` retired
        everywhere else. Those words are still its name for a reader who cannot
        see it. The Menu tab pressed on this screen leads to the same place
        (`menuTabHref`).
      */}
            <header className="gw-screen__header gw-screen__header--back">
              {back === null ? null : (
                <Link
                  href={back.href}
                  className="gw-screen__back"
                  aria-label={back.label}
                  title={back.label}
                >
                  <BackChevron />
                </Link>
              )}
              <h1>{storefront.shop?.displayName ?? pt('storefront.title')}</h1>
            </header>

            {/* Said once, in the platform's own words, so the operator's activity
                feed and this sentence cannot disagree. */}
            {shopChanged ? (
              <p className="gw-notice-danger" role="alert">
                {pt('error.shop_changed')}
              </p>
            ) : null}

            {/* The morning this counter was asked about, and the way to change it.
                Mode B2 states it under the address instead (`Shell`, GST-045). */}
            {bound ? null : <ChosenMorning backTo="/menu" />}

            {storefront.items.length === 0 ? (
              <Card title={t('menu.empty')} />
            ) : (
              sections.map((section) => (
                <Section key={section.type} title={sectionTitle(section.type, headings)}>
                  {section.items.map((item) => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      aboveTheFold={positionOf(item.id) < EAGER_TILES}
                      quantity={quantityFor(basket.lines, item.id)}
                      onAdd={() =>
                        asksSomething(item)
                          ? setOpenItemId(item.id)
                          : dispatch({
                              type: 'ADD',
                              itemId: item.id,
                              max: item.maxQuantityPerOrder,
                              name: nameOf(item),
                            })
                      }
                      onSetQuantity={(quantity) => {
                        const current = quantityFor(basket.lines, item.id);
                        if (!asksSomething(item)) {
                          dispatch({
                            type: 'SET_QUANTITY',
                            itemId: item.id,
                            quantity,
                            max: item.maxQuantityPerOrder,
                            name: nameOf(item),
                          });
                          return;
                        }
                        /* A set that ASKS something counts every answered line of it
                     on its tile, and its stepper knows none of the answers. One
                     MORE is a card to fill in, so the plus opens the sheet - a
                     bare SET_QUANTITY here minted an unanswered line at total+1
                     and the badge doubled. One FEWER comes off the line added
                     most recently. */
                        if (quantity > current) {
                          setOpenItemId(item.id);
                          return;
                        }
                        const last = lastLineOf(basket, item.id);
                        if (last === undefined) return;
                        dispatch({
                          type: 'SET_QUANTITY',
                          itemId: item.id,
                          quantity: last.quantity - (current - quantity),
                          max: item.maxQuantityPerOrder,
                          name: nameOf(item),
                          ...(last.choices === undefined ? {} : { choices: last.choices }),
                        });
                      }}
                      onOpen={() => setOpenItemId(item.id)}
                    />
                  ))}
                </Section>
              ))
            )}

            {openItem === undefined ? null : (
              <ItemDetail
                item={openItem}
                /* A set with a QUESTION on it opens on ZERO whatever is in the
             basket: its lines are distinguished by their answers, and the sheet
             has none yet. Everything else - a plain product, and a set that
             asks nothing and therefore has one line like any product - edits
             the one line it has. */
                quantity={asksSomething(openItem) ? 0 : quantityFor(basket.lines, openItem.id)}
                /* ...and for the same reason what it commits is ADDED to the basket:
             the formules already in it, with the same answers or others, stay.
             A sheet opened on its own line edits it. */
                onSetQuantity={(quantity, choices) =>
                  dispatch({
                    type: asksSomething(openItem) ? 'ADD_LINE' : 'SET_QUANTITY',
                    itemId: openItem.id,
                    quantity,
                    max: openItem.maxQuantityPerOrder,
                    name: nameOf(openItem),
                    ...(choices === undefined ? {} : { choices }),
                  })
                }
                onClose={() => setOpenItemId(null)}
              />
            )}

            {/*
        What is in the basket and what it comes to, on the control that opens
        it. The subtotal comes from `subtotalMinorUnits` - the one derivation of
        it, shared with the basket screen - and is the catalogue's own arithmetic,
        not a total: delivery, service and tax are the server's answer at review
        (GST-022), which is why this says "basket" and never "total".
      */}
            {/*
        The bar sizes to what it says and sits in the middle of the screen. A
        full-width control here was a wall between the guest and the shelf they
        were still reading; the four facts it carries are short, so the button
        is as wide as they are and no wider.
      */}
            {count > 0 ? (
              <div className="gw-stickybar gw-stickybar--center">
                {/*
            Same bar, same four facts, next step named. A mode B guest browsing
            before the picker has no morning yet, so the honest label is the
            thing the tap actually does - set the delivery time - and the picker
            carries `?next=/basket` so the round trip ends where "View basket"
            would have gone. Mode A, and mode B once the morning is set, is
            unchanged.
          */}
                <Button
                  onClick={() => (choiceAsked ? setChoiceNext('/basket') : navigate(basketStep))}
                >
                  <span className="gw-basketbar">
                    <span className="gw-basketbar__cart" aria-hidden="true">
                      <CartIcon size={20} />
                    </span>
                    <span className="gw-basketbar__count" aria-hidden="true">
                      {count}
                    </span>
                    {/* The label is read off the destination rather than re-deciding
                  it: a bar that says "View basket" and opens the picker is the
                  two-copies-of-one-rule bug in miniature. */}
                    <span className="gw-basketbar__label">
                      {choiceAsked
                        ? t('menu.scheduleDelivery')
                        : basketStep === '/basket'
                          ? t('menu.viewBasket')
                          : t('menu.setDeliveryTime')}
                    </span>
                    <span className="gw-basketbar__amount">
                      {money(subtotal, storefront.currency, locale)}
                    </span>
                  </span>
                </Button>
              </div>
            ) : null}

            <DeliveryChoice
              open={choiceOpen}
              next={choiceNext ?? '/menu'}
              onClose={() => {
                /* Closing the one the scan raised is remembered for the visit;
                   the bar still opens it, which is how the guest gets it back. */
                if (choiceNext === null) {
                  closedNoticeDismissed = true;
                  setNoticeClosed(true);
                }
                setChoiceNext(null);
              }}
            />
          </div>
        );
      }}
    </AsyncPanel>
  );
}

/**
 * A named group of the vitrine, laid out as the tile grid (GST-003). The grid
 * lives here rather than on the tile so a section is the thing that decides how
 * many columns its items get.
 */
function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <section className="cib-section">
      <h2 className="cib-section__title">{title}</h2>
      <div className="gw-menu-grid">{children}</div>
    </section>
  );
}

/**
 * The heading over a section.
 *
 * Every heading arrives resolved to this guest's language by the platform
 * (`Storefront.sections`), from the words authored on the internal Kinds tab -
 * resolved there rather than here because the section title and the product
 * names under it have to agree about what language this menu is in, and they
 * only do if one thing decides. There is no pair this app translates for
 * itself: `MENU` and `EXTRA` are kinds with words like every other (founder,
 * 2026-08-27). A kind the platform sent no heading for falls back to being its
 * own title, exactly as every storefront did before that tab existed (CAT-016).
 */
function sectionTitle(type: string, headings: Readonly<Record<string, string>>): string {
  const authored = headings[type];
  return authored !== undefined && authored !== '' ? authored : type;
}

/** Every line of a product, however it was answered - what a tile's badge counts. */
function quantityFor(
  lines: readonly { itemId: string; quantity: number }[],
  itemId: string,
): number {
  return lines.reduce((total, line) => (line.itemId === itemId ? total + line.quantity : total), 0);
}
